/**
 * Diagnóstico de Perda de Aderência — Pareto de motivos e planos corretivos.
 */

import type { LstExecucao, LstOcorrencia, LstStatus } from '../types';
import { decodeMotivoParada } from '../utils/kamishibaiStatus';
import type { EnterpriseAction } from '../../../services/actionService';

export type LossReasonKey =
  | 'emergencia_linha'
  | 'reuniao_extra'
  | 'falta_material'
  | 'processo_parado'
  | 'indisciplina'
  | 'desvio_nao_tratado'
  | 'outros';

export const LOSS_REASON_LABELS: Record<LossReasonKey, string> = {
  emergencia_linha: 'Emergências em Linha',
  reuniao_extra: 'Reuniões Extras',
  falta_material: 'Falta de Material / OP',
  processo_parado: 'Processo / Linha Parada',
  indisciplina: 'Indisciplina (não realizado)',
  desvio_nao_tratado: 'Desvio sem tratativa',
  outros: 'Outros motivos',
};

const LOSS_REASON_COLORS: Record<LossReasonKey, string> = {
  emergencia_linha: '#ef4444',
  reuniao_extra: '#f59e0b',
  falta_material: '#8b5cf6',
  processo_parado: '#64748b',
  indisciplina: '#dc2626',
  desvio_nao_tratado: '#eab308',
  outros: '#94a3b8',
};

export interface AdherenceLossParetoItem {
  key: LossReasonKey;
  label: string;
  count: number;
  pct: number;
  color: string;
}

export function classifyLossReason(
  status: LstStatus,
  justificativa: string | null | undefined,
): LossReasonKey {
  if (status === 'processo_parado' || decodeMotivoParada(justificativa)) {
    return 'processo_parado';
  }
  if (status === 'nao_realizado') {
    const j = (justificativa ?? '').toLowerCase();
    if (/emerg|parada|quebra|linha/i.test(j)) return 'emergencia_linha';
    if (/reuni|camada|extra/i.test(j)) return 'reuniao_extra';
    if (/material|op|setup/i.test(j)) return 'falta_material';
    return 'indisciplina';
  }
  if (status === 'realizado_com_desvio') {
    const j = (justificativa ?? '').toLowerCase();
    if (/emerg|parada/i.test(j)) return 'emergencia_linha';
    if (/reuni/i.test(j)) return 'reuniao_extra';
    if (!justificativa?.trim()) return 'desvio_nao_tratado';
    return 'outros';
  }
  return 'outros';
}

export function buildAdherenceLossPareto(ocorrencias: LstOcorrencia[]): AdherenceLossParetoItem[] {
  const counts = new Map<LossReasonKey, number>();
  for (const o of ocorrencias) {
    const key = classifyLossReason(o.status, o.justificativa);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  if (total === 0) return [];

  return [...counts.entries()]
    .map(([key, count]) => ({
      key,
      label: LOSS_REASON_LABELS[key],
      count,
      pct: Math.round((count / total) * 100),
      color: LOSS_REASON_COLORS[key],
    }))
    .sort((a, b) => b.count - a.count);
}

/** Filtra ações corretivas LST abertas para um líder ou setor. */
export function filterLstCorrectiveActions(
  actions: EnterpriseAction[],
  userId: string,
): EnterpriseAction[] {
  return actions.filter((a) => {
    if (a.status === 'completed' || a.status === 'cancelled') return false;
    const meta = a.metadata ?? {};
    const source = meta.source as string | undefined;
    const lstUser = meta.lst_user_id as string | undefined;
    const assigned = a.assigned_to;
    if (source === 'lst' || source === 'lst_tpl') {
      return assigned === userId || lstUser === userId || !assigned;
    }
    if (a.title.toLowerCase().includes('tpl') || a.title.toLowerCase().includes('aderência')) {
      return assigned === userId;
    }
    return false;
  });
}

/** Monta pareto a partir das execuções de um dia (fallback quando não há ocorrências semanais). */
export function buildParetoFromExecucoes(
  execucoes: LstExecucao[],
  templateTitles: Map<string, string>,
): AdherenceLossParetoItem[] {
  const pseudo: LstOcorrencia[] = execucoes
    .filter((e) => e.status === 'nao_realizado' || e.status === 'realizado_com_desvio' || e.status === 'processo_parado')
    .map((e) => ({
      date: e.data,
      templateTitle: templateTitles.get(e.template_id) ?? 'Rotina',
      status: e.status,
      justificativa: e.justificativa_desvio,
      isCritical: false,
    }));
  return buildAdherenceLossPareto(pseudo);
}
